import { isAnimalActive, isAnimalSuspicious } from "./animalUtils";
import { getTimestampInMillis } from "./epochMillis";
import { isHealthReviewCurrent } from "./healthReview";
import { isTreatmentCompleted } from "./treatmentStatus";

export const DEFAULT_RECENT_WINDOW_DAYS = 7;
export const DEFAULT_RECENT_WINDOW_MS =
	DEFAULT_RECENT_WINDOW_DAYS * 24 * 60 * 60 * 1000;

// Kept as a named export: dashboardViewModel and others import it from here.
export { getTimestampInMillis };

function getAnimalMac(animal) {
	return animal?.MAC ?? animal?.Mac ?? animal?.mac ?? null;
}

function getTreatmentMac(treatment) {
	return treatment?.Mac ?? treatment?.MAC ?? treatment?.mac ?? null;
}

/**
 * A treatment is active while it is neither archived nor completed.
 * Completion is delegated to utils/treatmentStatus — the private
 * re-implementation this replaced diverged from every other surface.
 */
function isTreatmentActive(treatment) {
	if (!treatment || treatment.Archived) {
		return false;
	}
	return !isTreatmentCompleted(treatment);
}

function getTreatmentStartTimeMs(treatment) {
	if (!treatment) return null;
	// Prefer explicit seconds field; fall back to generic record timestamp.
	return getTimestampInMillis(
		treatment.StartDateSeconds ?? treatment.StartDate ?? treatment.t
	);
}

function getAnimalDiagnosisTimeMs(animal) {
	if (!animal) return null;
	return getTimestampInMillis(
		animal?.Diagnosed?.t ??
			animal?.Diagnosed?.StartDateSeconds ??
			animal?.DiagnosedAt
	);
}

function buildRecentlyHandledMap({ animals, treatments, nowMs, recentWindowMs }) {
	const handled = new Map();

	if (Array.isArray(animals)) {
		animals.forEach((animal) => {
			const mac = getAnimalMac(animal);
			if (!mac) return;

			const diagnosisTime = getAnimalDiagnosisTimeMs(animal);
			if (
				typeof diagnosisTime === "number" &&
				nowMs - diagnosisTime < recentWindowMs
			) {
				handled.set(mac, {
					...(handled.get(mac) || {}),
					recentlyDiagnosed: true,
				});
			}

			// "Befund zur Kenntnis nehmen": the review counts only while the
			// Watchdog evidence it was given for is unchanged (see healthReview.js).
			if (isHealthReviewCurrent(animal, { nowMs, windowMs: recentWindowMs })) {
				handled.set(mac, {
					...(handled.get(mac) || {}),
					reviewed: true,
				});
			}
		});
	}

	if (Array.isArray(treatments)) {
		treatments.forEach((treatment) => {
			const mac = getTreatmentMac(treatment);
			if (!mac || treatment?.Archived) return;

			const existing = handled.get(mac) || {};

			if (isTreatmentActive(treatment)) {
				handled.set(mac, { ...existing, underTreatment: true });
				return;
			}

			const startTime = getTreatmentStartTimeMs(treatment);
			if (
				typeof startTime === "number" &&
				nowMs - startTime < recentWindowMs
			) {
				handled.set(mac, { ...existing, recentlyTreated: true });
			}
		});
	}

	return handled;
}

/**
 * Returns suspicious animals that "need attention" with an optional exclusion rule:
 * exclude animals that were recently diagnosed / are under treatment / were
 * recently treated / whose finding was acknowledged and has not changed since.
 */
export function getSuspiciousAnimalsForAttention({
	animals = [],
	treatments = [],
	nowMs = Date.now(),
	recentWindowMs = DEFAULT_RECENT_WINDOW_MS,
	excludeRecentlyHandled = true,
} = {}) {
	const recentlyHandledMap = excludeRecentlyHandled
		? buildRecentlyHandledMap({ animals, treatments, nowMs, recentWindowMs })
		: new Map();

	const excludedCounts = {
		recentlyDiagnosed: 0,
		underTreatment: 0,
		recentlyTreated: 0,
		reviewed: 0,
	};

	const suspiciousAnimals = Array.isArray(animals)
		? animals.filter((animal) => {
				if (!isAnimalSuspicious(animal) || !isAnimalActive(animal)) {
					return false;
				}

				if (!excludeRecentlyHandled) {
					return true;
				}

				const mac = getAnimalMac(animal);
				if (!mac) {
					return true;
				}

				const handledInfo = recentlyHandledMap.get(mac);
				if (!handledInfo) {
					return true;
				}

				if (handledInfo.recentlyDiagnosed)
					excludedCounts.recentlyDiagnosed += 1;
				if (handledInfo.underTreatment) excludedCounts.underTreatment += 1;
				if (handledInfo.recentlyTreated) excludedCounts.recentlyTreated += 1;
				if (handledInfo.reviewed) excludedCounts.reviewed += 1;

				return !(
					handledInfo.recentlyDiagnosed ||
					handledInfo.underTreatment ||
					handledInfo.recentlyTreated ||
					handledInfo.reviewed
				);
		  })
		: [];

	return {
		suspiciousAnimals,
		excludedCounts,
		rules: {
			excludeRecentlyHandled,
			recentWindowMs,
		},
	};
}

